import * as React from 'react';
import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  ListSubheader,
  Checkbox,
  ListItemText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const ITEM_HEIGHT = 50;
const ITEM_PADDING_TOP = 4;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
  autoFocus: false,
};

const SELECT_ALL = "__SELECT_ALL__";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    width: '100%',
  },
  input: {
    width: '100%',
    '& .MuiOutlinedInput-input': {
      padding: '12px 14px',
      fontSize: '16px',
      borderColor: 'blue',
      height: 'auto',
      '&:focus': {
        borderColor: 'green',
      },
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    maxHeight: 64,
    overflowY: 'auto',
  },
}));

const MultipleSelectChip = ({
  options = [],
  label = 'Select',
  value = [],
  onChange,
  searchEnabled = true,
  placeholder = 'Search...',
  searchQuery = '',
  onSearchQueryChange = () => {},
  selectAllEnabled = false,
}) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const chipContainerRef = React.useRef(null);

  const handleMenuOpen = () => setOpen(true);
  const handleMenuClose = () => setOpen(false);

  const handleSearchKeyDown = (event) => {
    event.stopPropagation();
  };

  const filteredOptions = (options || []).filter(
    (option) =>
      typeof option === "string" &&
      option.toLowerCase().includes((searchQuery || "").toLowerCase())
  );

  const handleChange = (event) => {
    const { target: { value: newValue } } = event;
    const finalValue =
      typeof newValue === 'string' ? newValue.split(',') : newValue;

    if (finalValue.includes(SELECT_ALL)) {
      if (value.length === filteredOptions.length) {
        onChange([]);
      } else {
        onChange(filteredOptions);
      }
    } else {
      onChange(finalValue);
    }
  };

  const isAllSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => value.includes(opt));

  const isIndeterminate =
    value.length > 0 &&
    value.length < filteredOptions.length;

  const handleChipContainerClick = (event) => {
    setOpen(true);
  };

  return (
    <FormControl className={classes.formControl}>
      <Select
        labelId="multiple-chip-label"
        id="multiple-chip"
        multiple
        value={value}
        style={{ minHeight: 50, width: 260 }}
        onChange={handleChange}
        input={
          <OutlinedInput
            id="select-multiple-chip"
            label={label}
            className={classes.input}
          />
        }
        renderValue={(selected) => (
          <Box
            className={classes.chipContainer}
            ref={chipContainerRef}
            onMouseDown={handleChipContainerClick}
          >
            {selected.map((val) => (
              <Chip key={val} label={val} />
            ))}
          </Box>
        )}
        MenuProps={MenuProps}
        open={open}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
      >
        {searchEnabled && (
          <ListSubheader>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={onSearchQueryChange}
              label={placeholder}
              variant="outlined"
              size="small"
              autoFocus
              onKeyDown={handleSearchKeyDown}
            />
          </ListSubheader>
        )}

        {selectAllEnabled && filteredOptions.length > 0 && (
          <MenuItem value={SELECT_ALL}>
            <Checkbox
              checked={isAllSelected}
              indeterminate={isIndeterminate}
              color="primary"
            />
            <ListItemText primary="Select All" />
          </MenuItem>
        )}

        {filteredOptions.length === 0 ? (
          <MenuItem disabled>No results found</MenuItem>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox
                checked={value.indexOf(option) > -1}
                color="primary"
              />
              <ListItemText primary={option} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default MultipleSelectChip;